import React, { useEffect, useState, useRef, memo } from "react";
import { Alert, FormControl, CircularProgress, Tooltip, Zoom, Chip, } from "@mui/material";
import { api } from "../../../services/api";
import { Info, InfoOutlined, CloseOutlined, ErrorOutlineOutlined, AddOutlined, Check, UploadFileOutlined, } from "@mui/icons-material";

const ImageSection = ({ onMediaChange, onErrorChange, error }) => {
  const refSeccion = useRef({});
  const [expandedSection, setExpandedSection] = useState(false);
  const [mediaUrls, setMediaUrls] = useState([]);
  const [mediaTypes, setMediaTypes] = useState([]);
  const [mediaFormats, setMediaFormats] = useState([]);
  const [mediaId, setMediaId] = useState([]);
  const [selectedFileIndex, setSelectedFileIndex] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSectionClick = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (mediaUrls.length > 0) {
      setSelectedFileIndex(0);
    } else {
      setSelectedFileIndex(null);
    }
  }, [mediaUrls]);

  const handleAddFileClick = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*,video/*";
    fileInput.multiple = false;
    fileInput.onchange = handleFileChange;
    fileInput.click();
  };

  const handleFileChange = async (e) => {
    const maxSizeFile = 50 * 1024 * 1024;
    const maxFiles = 10;
    const selectedFiles = Array.from(e.target.files);

    if (files.length + selectedFiles.length > maxFiles) {
      alert(`Solo puedes subir como máximo ${maxFiles} archivos.`);
      return;
    }

    const validaFiles = [];
    selectedFiles.forEach((file) => {
      if (file.size <= maxSizeFile) {
        validaFiles.push(file);
      } else {
        console.error(
          `El archivo ${file.name} supera el límite de tamaño de 50MB`
        );
        alert(`El archivo ${file.name} supera el límite de tamaño de 50MB`);
      }
    });

    if (validaFiles.length === 0) return;

    setLoading(true);

    setFiles((prevFiles) => {
      const combinedFiles = [...prevFiles, ...validaFiles];

      if (combinedFiles.length > maxFiles) {
        alert(`Solo puedes subir como máximo ${maxFiles} archivos.`);
        return prevFiles;
      }

      if (selectedFileIndex === null && validaFiles.length > 0) {
        setSelectedFileIndex(prevFiles.length);
      }

      return combinedFiles;
    });

    const formData = new FormData();
    validaFiles.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await api.media.upload(formData);
      if (response.status === "success") {
        const medias = response.data.media;
        setMediaUrls((prevUrls) => [
          ...prevUrls,
          ...medias.map((media) => media.url),
        ]);
        setMediaTypes((prevTypes) => [
          ...prevTypes,
          ...medias.map((media) => media.type),
        ]);
        setMediaFormats((prevFormats) => [
          ...prevFormats,
          ...medias.map((media) => media.format),
        ]);
        setMediaId((prevMediaIds) => [
          ...prevMediaIds,
          ...medias.map((media) => media.public_id),
        ]);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleThumbnailClick = (index) => {
    setSelectedFileIndex(index);
  };

  useEffect(() => {
    const mediaData = {
      files,
      mediaUrls,
      mediaTypes,
      mediaFormats,
      mediaId,
    };
  
    if (files.length > 0 || mediaUrls.length > 0) { //solo si hay archivos 
      onMediaChange(mediaData);
    }
  }, [files, mediaUrls, mediaTypes, mediaFormats, mediaId, onMediaChange]);
  
  useEffect(() => {
    if (error) {
      onErrorChange(error);
    }
  }, [error, onErrorChange]); 

  const handleDragStart = (event, index) => {
    event.dataTransfer.setData("text/plain", index);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event, targetIndex) => {
    event.preventDefault();

    const sourceIndex = parseInt(event.dataTransfer.getData("text/plain"), 10);

    if (sourceIndex !== targetIndex) {
      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles];
        const [movedFile] = updatedFiles.splice(sourceIndex, 1);
        updatedFiles.splice(targetIndex, 0, movedFile);
        return updatedFiles;
      });
      setMediaId((prevMediaIds) => {
        const updatedFilesId = [...prevMediaIds];
        const [movedFile] = updatedFilesId.splice(sourceIndex, 1);
        updatedFilesId.splice(targetIndex, 0, movedFile);
        return updatedFilesId;
      });

      if (selectedFileIndex === sourceIndex) {
        setSelectedFileIndex(targetIndex);
      } else if (
        selectedFileIndex > sourceIndex &&
        selectedFileIndex <= targetIndex
      ) {
        setSelectedFileIndex(selectedFileIndex - 1);
      } else if (
        selectedFileIndex < sourceIndex &&
        selectedFileIndex >= targetIndex
      ) {
        setSelectedFileIndex(selectedFileIndex + 1);
      }
    }
  };

  const renderFilePreview = (file, index) => {
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    const fileURL = URL.createObjectURL(file);

    const getThumbnailBadge = () => {
      if (files.length === 1)
        return (
          <Tooltip
            arrow
            TransitionComponent={Zoom}
            placement="right"
            title="Imagen de perfil y de portada"
          >
            <Info
              sx={{
                display: "flex",
                width: "18px",
                height: "18px",
                marginLeft: "5px",
                position: "absolute",
                color: "#FFD400",
                top: "5px",
                right: "5px",
              }}
            />
          </Tooltip>
        );
      if (index === 0)
        return (
          <Tooltip
            arrow
            TransitionComponent={Zoom}
            placement="right"
            title="Imagen de perfil"
          >
            <Info
              sx={{
                display: "flex",
                width: "18px",
                height: "18px",
                marginLeft: "5px",
                position: "absolute",
                color: "#FFD400",
                top: "5px",
                right: "5px",
              }}
            />
          </Tooltip>
        );
      if (index === 1)
        return (
          <Tooltip
            arrow
            TransitionComponent={Zoom}
            placement="right"
            title="Imagen de portada"
          >
            <Info
              sx={{
                display: "flex",
                width: "18px",
                height: "18px",
                marginLeft: "5px",
                position: "absolute",
                color: "#FFD400",
                top: "5px",
                right: "5px",
              }}
            />
          </Tooltip>
        );
      if (index >= 2)
        return (
          <Tooltip
            arrow
            TransitionComponent={Zoom}
            placement="right"
            title="Imagen de descripcion"
          >
            <Info
              sx={{
                display: "flex",
                width: "18px",
                height: "18px",
                marginLeft: "5px",
                position: "absolute",
                color: "#FFD400",
                top: "5px",
                right: "5px",
              }}
            />
          </Tooltip>
        );

      return null;
    };

    const thumbnailClass =
      selectedFileIndex === index ? "thumbnail selected" : "thumbnail";

    return (
      <div
        key={index}
        className={thumbnailClass}
        draggable
        onDragStart={(e) => handleDragStart(e, index)}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, index)}
        onClick={() => handleThumbnailClick(index)}
      >
        {getThumbnailBadge()}
        {isImage && <img src={fileURL} alt={`Preview ${index}`} />}
        {isVideo && <video src={fileURL} />}
      </div>
    );
  };

  const deleteMedia = async (index) => {
    const publicId = mediaId[index];

    try {
      const response = await api.media.delete({ public_id: publicId });
      if (response.status === "success") {
        setFiles((prevFiles) =>
          prevFiles.filter((file) => file.public_id !== publicId)
        );
        setMediaUrls((prevUrls) =>
          prevUrls.filter((_, i) => mediaId[i] !== publicId)
        );
        setMediaTypes((prevTypes) =>
          prevTypes.filter((_, i) => mediaId[i] !== publicId)
        );
        setMediaFormats((prevFormats) =>
          prevFormats.filter((_, i) => mediaId[i] !== publicId)
        );
        setMediaId((prevMediaIds) =>
          prevMediaIds.filter((id) => id !== publicId)
        );

        if (selectedFileIndex === publicId) {
          setSelectedFileIndex(null);
        }
      }
    } catch (error) {
      console.error("Error deleting media:", error);
    }
  };

  const renderMainPreview = () => {
    if (selectedFileIndex === null || files.length === 0) return null;

    const selectedFile = files[selectedFileIndex];
    const isImage = selectedFile.type.startsWith("image/");
    const isVideo = selectedFile.type.startsWith("video/");
    const fileURL = URL.createObjectURL(selectedFile);

    const getTooltipText = () => {
      if (files.length === 1)
        return (
          <Tooltip
            arrow
            TransitionComponent={Zoom}
            placement="left"
            title="Esta imagen sera utilizada como imagen de perfil y la portada del evento"
          >
            <Info
              sx={{
                width: "25px",
                height: "25px",
                marginLeft: "5px",
                position: "absolute",
                color: "#FFD400",
                top: "10px",
                left: "1%",
                zIndex: "200",
              }}
            />
          </Tooltip>
        );
      if (selectedFileIndex === 0)
        return (
          <Tooltip
            arrow
            TransitionComponent={Zoom}
            placement="left"
            title="Esta imagen sera utilizada como imagen de perfil del evento"
          >
            <Info
              sx={{
                width: "25px",
                height: "25px",
                marginLeft: "5px",
                position: "absolute",
                color: "#FFD400",
                top: "10px",
                left: "1%",
                zIndex: "200",
              }}
            />
          </Tooltip>
        );
      if (selectedFileIndex === 1)
        return (
          <Tooltip
            arrow
            TransitionComponent={Zoom}
            placement="left"
            title="Esta imagen sera utilizada como la imagen de portada del evento"
          >
            <Info
              sx={{
                width: "25px",
                height: "25px",
                marginLeft: "5px",
                position: "absolute",
                color: "#FFD400",
                top: "10px",
                left: "1%",
                zIndex: "200",
                display: "block",
              }}
            />
          </Tooltip>
        );
      if (selectedFileIndex >= 2)
        return (
          <Tooltip
            arrow
            TransitionComponent={Zoom}
            placement="left"
            title="Esta imagen sera utilizada en la descripcion del evento"
          >
            <Info
              sx={{
                width: "25px",
                height: "25px",
                marginLeft: "5px",
                position: "absolute",
                color: "#FFD400",
                top: "10px",
                left: "1%",
                zIndex: "200",
              }}
            />
          </Tooltip>
        );
      return null;
    };

    const handleRemoveFile = () => {
      setFiles((prevFiles) =>
        prevFiles.filter((_, index) => index !== selectedFileIndex)
      );
      setSelectedFileIndex(null);
    };

    return (
      <div className="main-preview">
        {isImage && <img src={fileURL} alt="Imagen seleccionada" />}
        {isVideo && <video src={fileURL} controls />}
        <div className="tooltip-fotos">{getTooltipText()}</div>
        <button
          type="button"
          className="remove-button"
          onClick={() => {
            deleteMedia(selectedFileIndex);
            handleRemoveFile();
          }}
        >
          Eliminar
        </button>
      </div>
    );
  };

  return (
    <>
      <div
        ref={(el) => (refSeccion.current["img"] = el)}
        className={`seccion-evento ${
          expandedSection === "img" ? "expanded" : ""
        }`}
        onClick={() => handleSectionClick("img")}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex justify-content-between align-items-center gap-3">
            <p
              className={`titulo-seccion-evento ${
                expandedSection === "img" ? "hidden" : ""
              }`}
            >
              Contenido visual
            </p>
            <Chip
              className={`${expandedSection === "img" ? "hidden" : ""}`}
              icon={<Check sx={{ color: "#5656FF !important" }} />}
              size="small"
              label="Requerido"
              sx={{ backgroundColor: "#E6E6FF", color: "#5656FF" }}
            />
          </div>
          {error.files && (
            <ErrorOutlineOutlined
              sx={{
                color: "red",
                display: expandedSection === "img" ? "none" : "block",
              }}
            />
          )}
        </div>

        <p
          className={`descrip-seccion-evento ${
            expandedSection === "img" ? "hidden" : ""
          }`}
        >
          Sube imágenes y videos para destacar los momentos más importantes de tu evento.
        </p>

        {expandedSection === "img" && (
          <div className="contenido-evento">
            <FormControl onClick={handleInputClick}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <p className="titulo-seccion-evento-expanded">
                  Agregar imágenes y videos{" "}
                  <span className="text-danger">*</span>
                </p>
                <button
                  type="button"
                  onClick={() => handleSectionClick("img")}
                  className="d-flex justify-content-end"
                >
                  <CloseOutlined sx={{ color: "#3659E3" }} />
                </button>
              </div>

              <p
                className="descrip-seccion-evento-expanded"
                style={{ fontSize: "16px", lineHeight: "24px" }}
              >
                Sube <b>hasta 10 imágenes y/o videos</b> que muestren los
                momentos clave de tu evento. <br />
                <span /* style={{color:"#5656FF"}} */>
                  Asegúrate de elegir archivos de <b>alta calidad </b> para un
                  gran impacto visual.
                </span>
              </p>
              <p
                className="descrip-seccion-evento-expanded"
                style={{ fontSize: "16px", marginBottom: "0" }}
              >
                Selecciona y organiza el orden de tus imágenes cuidadosamente.{" "}
              </p>
              <div>
                <ul>
                  <li>
                    La <b>primera imagen</b> será la foto principal del evento.
                  </li>
                  <li>
                    La <b>segunda</b> será la portada del evento.
                  </li>
                  <li>
                    Las demás se mostrarán en la descripción, agregando más
                    detalles visuales.
                  </li>
                </ul>
              </div>
              <div className="info-input-img">
                <div className="d-flex gap-2 justify-content-center align-items-center w-100 flex-wrap">
                  {/* <Chip
                    icon={<InfoOutlined sx={{ color: "#5656FF !important" }} />}
                    size="small"
                    label="Tamaño recomendado de imagen: 1920x1280"
                    sx={{ backgroundColor: "#E6E6FF", color: "#5656FF" }}
                  /> */}
                  <Chip
                    icon={<InfoOutlined sx={{ color: "#5656FF !important" }} />}
                    size="small"
                    label="Tamaño máximo de archivo: 10MB"
                    sx={{ backgroundColor: "#E6E6FF", color: "#5656FF" }}
                  />
                  <Chip
                    icon={<InfoOutlined sx={{ color: "#5656FF !important" }} />}
                    size="small"
                    label="Formatos admitidos de imagen: JPEG, PNG"
                    sx={{ backgroundColor: "#E6E6FF", color: "#5656FF" }}
                  />
                </div>
              </div>

              <div
                className={`input-file ${files.length === 0 ? "" : "border-0"}`}
              >
                <input
                  id="files"
                  name="files"
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleFileChange}
                  onClick={handleInputClick}
                  style={{ display: "none" }}
                  required
                />

                {loading ? (
                  <div className="d-flex flex-column align-items-center">
                    <p>Cargando archivos...</p>
                    <CircularProgress />
                  </div>
                ) : files.length === 0 ? (
                  <label className="placeholder-input-img" htmlFor="files">
                    <UploadFileOutlined
                      sx={{
                        width: "46px",
                        height: "46px",
                        color: "#5656ff",
                        "&:hover": {
                          color: "#221A60",
                        },
                      }}
                    />
                    Haz clic aquí para subir una imagen/video
                  </label>
                ) : (
                  <>
                    <div className="imagen-contenedor">
                      {renderMainPreview()}
                      <div className="thumbnails-contenedor">
                        <div
                          className="boton-agregar-img"
                          onClick={handleAddFileClick}
                        >
                          <AddOutlined sx={{ color: "#3659E3" }} />
                        </div>
                        {files.map(renderFilePreview)}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <>
                {error.files && (
                  <Alert
                    severity="error"
                    sx={{
                      marginTop: "10px",
                    }}
                  >
                    {error.files}
                  </Alert>
                )}
              </>
            </FormControl>
          </div>
        )}
      </div>
    </>
  );
};

export default memo(ImageSection);
